"use client";

import { createInstance, type i18n as I18nInstance } from "i18next";
import { I18nextProvider } from "react-i18next";
import { initReactI18next } from "react-i18next";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { createLogger } from "@/app/lib/logger";
import type { Translations } from "@/app/utils/i18next";
import { loadTranslations } from "@/app/utils/i18next/load";
import {
  SUPPORTED_LOCALES,
  DEFAULT_LOCALE,
  getLocaleFromPath,
  isLocaleExcludedPath,
  type SupportedLocale,
} from "@/app/lib/i18n-config";

const log = createLogger("I18nProvider");

/**
 * Create an i18next instance that is ready on the first render: the locale the
 * server resolved for this request is active and its bundle (passed down from
 * the server) is loaded synchronously, so /en, /fr, ... serve HTML in their own
 * language and the hydration render matches it. Other locales load on demand.
 *
 * Each provider owns its instance. A module-level instance would be shared by
 * concurrent requests on the server, leaking one request's language into another.
 */
export function createI18n(
  locale: SupportedLocale,
  translations: Translations
): I18nInstance {
  const instance = createInstance();
  instance.use(initReactI18next).init({
    resources: {
      [locale]: { translation: translations },
    },
    lng: locale,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...SUPPORTED_LOCALES],
    initAsync: false,
    interpolation: {
      escapeValue: false,
    },
  });
  return instance;
}

interface I18nProviderProps {
  children: React.ReactNode;
  /** Locale the root layout resolved for this request (also `<html lang>`). */
  locale: SupportedLocale;
  /** Translations for `locale`; the client bundle ships none of its own. */
  translations: Translations;
}

export default function I18nProvider({
  children,
  locale,
  translations,
}: I18nProviderProps) {
  const pathname = usePathname();
  const [i18n] = useState(() => createI18n(locale, translations));

  useEffect(() => {
    // Blog and job pages set the language from their own content
    if (isLocaleExcludedPath(pathname)) return;

    // The URL is the only source of the UI language, the same one the
    // LanguageSwitcher and LocalizedLink read, so they can never disagree
    const targetLang = getLocaleFromPath(pathname);
    if (i18n.language === targetLang) return;

    // A newer pathname cancels this load so a slow bundle can't win
    let cancelled = false;
    const apply = async () => {
      try {
        if (!i18n.hasResourceBundle(targetLang, "translation")) {
          const translation = await loadTranslations(targetLang);
          if (cancelled) return;
          i18n.addResourceBundle(
            targetLang,
            "translation",
            translation,
            true,
            true
          );
        }
        if (!cancelled) await i18n.changeLanguage(targetLang);
      } catch (error) {
        log.error("Failed to load translations", error, {
          target_language: targetLang,
        });
      }
    };
    apply();

    return () => {
      cancelled = true;
    };
  }, [pathname, i18n]);

  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
}
